import { useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  resetPasswordFormSchema,
  resetPasswordRequestSchema,
  type ResetPasswordForm,
} from "@nh/shared";
import {
  Alert,
  AuthCard,
  AuthLayout,
  Button,
  Form,
  FormError,
  PasswordField,
  SubmitButton,
} from "@/shared/ui/core";
import { resetPasswordRequest } from "./auth.api";
import { emailLinkErrorMessage, isEmailLinkRejection } from "./auth.errors";
import { AuthSwitchLink } from "./components/AuthSwitchLink";
import { useAuthSubmit } from "./useAuthSubmit";

/**
 * Lo que se dice cuando la URL no trae token, o trae uno que ni siquiera
 * tiene la forma de un token.
 *
 * NO dice "expiró" ni "ya lo usaste": aquí no ha hablado el servidor
 * todavía, así que lo único que se sabe es que el enlace está incompleto.
 * Lo más probable es lo que dice —un enlace partido al copiarlo del
 * correo— y es lo único que se puede afirmar sin inventar.
 */
const BROKEN_LINK_MESSAGE =
  "Este enlace no trae la llave que hace falta. Puede que se haya cortado " +
  "al copiarlo del correo. Pide otro y ábrelo directamente desde ahí.";

/**
 * El estado de éxito. Menciona las OTRAS sesiones a propósito.
 *
 * El backend revoca todas al restablecer —es lo correcto: si alguien había
 * entrado con la contraseña vieja, cambiarla tiene que echarlo—, y quien no
 * lo sepa va a pensar que su teléfono se rompió cuando le pida entrar otra
 * vez esta tarde.
 */
const SUCCESS_MESSAGE =
  "Ya puedes entrar con tu contraseña nueva. Cerramos las demás sesiones " +
  "que tenías abiertas, así que en tus otros dispositivos habrá que volver " +
  "a entrar.";

/**
 * En qué momento de la tarea está la pantalla.
 *
 * `rejected` guarda su propio mensaje porque llega de dos sitios que dicen
 * cosas distintas: el enlace roto que se detecta aquí, y el que el
 * servidor rechaza al enviar.
 */
type Phase =
  | { kind: "form" }
  | { kind: "rejected"; message: string }
  | { kind: "done" };

/**
 * `/restablecer?token=…` — escribir la contraseña nueva.
 *
 * ── Sin token no hay formulario ─────────────────────────────────────
 * Lo primero que hace esta pantalla es mirar si el enlace trae token, y
 * si no lo trae NO pinta el formulario. Enseñar dos casillas de
 * contraseña que van a fallar al enviar hace perder el tiempo dos veces:
 * una escribiendo, otra leyendo el error. La forma del token no se
 * comprueba a ojo aquí, se le pregunta a `resetPasswordRequestSchema` —el
 * mismo esquema que valida el servidor—, así que si el mínimo cambia,
 * cambia en un solo sitio.
 *
 * Lo que esta comprobación NO puede saber es si el token sirve: eso solo
 * lo sabe el servidor. Por eso hay dos caminos al mismo estado.
 *
 * ── El token viaja en el CUERPO ─────────────────────────────────────
 * Está en la URL de ESTA pantalla porque el correo tiene que traerlo de
 * algún modo, pero la llamada al API lo manda en el cuerpo. Una URL de
 * API con el token dentro quedaría en los logs de cualquier proxy del
 * camino y en el `Referer` que el navegador manda al siguiente sitio.
 *
 * ── No se entra solo ────────────────────────────────────────────────
 * Al terminar NO hay sesión: hay un botón para ir a entrar. Restablecer
 * la contraseña es una operación de recuperación, y quien la hizo debe
 * demostrar que sabe la contraseña nueva antes de estar dentro.
 */
export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const token = searchParams.get("token") ?? "";

  const form = useForm<ResetPasswordForm>({
    resolver: zodResolver(resetPasswordFormSchema),
    // El token es un valor del formulario sin campo que lo pinte: así
    // `resetPasswordFormSchema` lo valida junto con lo demás y llega al
    // envío por el mismo camino, sin plomería aparte.
    defaultValues: { token, password: "", confirmPassword: "" },
    mode: "onSubmit",
  });

  const [phase, setPhase] = useState<Phase>(() =>
    resetPasswordRequestSchema.shape.token.safeParse(token).success
      ? { kind: "form" }
      : { kind: "rejected", message: BROKEN_LINK_MESSAGE },
  );

  const { submit, formError, dismissFormError } = useAuthSubmit({
    form,
    // `token` queda FUERA a propósito: no tiene campo donde pintarse, y un
    // error puesto en un campo que no existe es un error invisible.
    fields: ["password", "confirmPassword"],
    // Se arma el cuerpo campo por campo y NO se reenvía `values`:
    // `confirmPassword` es asunto de la interfaz —al servidor no le sirve
    // recibir dos veces lo mismo— y mandarlo sería dejar la contraseña
    // escrita un segundo lugar más en los logs.
    action: ({ token: formToken, password }) =>
      resetPasswordRequest({ token: formToken, password }),
    onError: (error) => {
      // Un servidor caído NO es un enlace muerto: el token sigue siendo
      // bueno y lo que toca es reintentar, no pedir otro correo. Por eso
      // solo el rechazo del enlace cambia de estado; lo demás cae en el
      // aviso de arriba y deja el formulario donde está.
      if (!isEmailLinkRejection(error)) return false;

      // Con el token muerto, volver al formulario solo invita a escribir
      // la contraseña otra vez para que falle igual. El mensaje va TAL
      // CUAL como lo mandó el servidor: ver `emailLinkErrorMessage`.
      setPhase({ kind: "rejected", message: emailLinkErrorMessage(error) });
      return true;
    },
    onSuccess: () => setPhase({ kind: "done" }),
  });

  return (
    // `sm`, no `md`: aquí hay dos campos cortos. Con el ancho del login la
    // card queda con casillas enormes y aire de sobra a los lados.
    <AuthLayout width="sm">
      <AuthCard
        title={TITLES[phase.kind]}
        subtitle={phase.kind === "form" ? SUBTITLE : undefined}
        footer={
          // En el éxito NO hay pie: el botón de arriba ya lleva al mismo
          // sitio, y un segundo enlace al login obliga a decidir entre dos
          // caminos idénticos. Además "¿Ya la recordaste?" ahí sería
          // absurdo — la acaba de cambiar.
          phase.kind === "done" ? undefined : (
            <AuthSwitchLink
              prompt="¿Ya la recordaste?"
              linkLabel="Inicio de sesión"
              to="/login"
            />
          )
        }
      >
        {phase.kind === "rejected" && (
          <div className="grid gap-4">
            <Alert variant="error">{phase.message}</Alert>
            <Button
              variant="primary"
              size="lg"
              shape="rounded"
              fullWidth
              onClick={() => navigate("/forgot-password")}
            >
              Pedir otro enlace
            </Button>
          </div>
        )}

        {phase.kind === "done" && (
          <div className="grid gap-4">
            <Alert variant="success">{SUCCESS_MESSAGE}</Alert>
            <Button
              variant="primary"
              size="lg"
              shape="rounded"
              fullWidth
              onClick={() => navigate("/login", { replace: true })}
            >
              Ir a inicio de sesión
            </Button>
          </div>
        )}

        {phase.kind === "form" && (
          <>
            {/* Fuera del `<Form>`, igual que en el login: no es un campo,
                es el resultado del último envío. */}
            <FormError error={formError} onDismiss={dismissFormError} />

            <Form form={form} onSubmit={submit} className="grid gap-4">
              <PasswordField
                label="Contraseña nueva"
                purpose="new"
                showStrength
                autoFocus
                required
              />
              <PasswordField
                name="confirmPassword"
                label="Confirmar contraseña"
                purpose="new"
                required
              />
              <SubmitButton size="lg" shape="rounded" fullWidth>
                Guardar la contraseña
              </SubmitButton>
            </Form>
          </>
        )}
      </AuthCard>
    </AuthLayout>
  );
}

/**
 * Neutro en los dos caminos que llevan a `rejected`. "Ese enlace expiró"
 * sería mentira la mitad de las veces: el servidor responde igual si
 * caducó, si nunca existió o si ya se usó, y aquí no hay forma de saber
 * cuál fue.
 */
const TITLES: Record<Phase["kind"], string> = {
  form: "Crea una contraseña nueva",
  rejected: "Ese enlace no funciona",
  done: "Contraseña actualizada",
};

const SUBTITLE = "Escríbela dos veces para que no se cuele una errata.";
